import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RiImageLine,
  RiPlayCircleLine,
  RiTeamLine,
  RiMoneyPoundCircleLine,
  RiCalendarLine,
  RiUserHeartLine,
} from 'react-icons/ri';
import { Link, Button, Tile } from '@Components/common';
import { StatusChip } from '@Components';
import { Tooltip } from '@Components';
import { InvitationStatusChip } from '@Views/FundManagement/InvitationStatusChip';
import { SelectionStatusEnum, ProjectStatusEnum } from '@Types';
import styles from './ProjectItem.module.scss';
import { ButtonProps } from '@Components/common/Button/Button';
import { Routes } from '@App/paths';
import { ProjectInformation, ProjectInformationItem } from './Components/ProjectInformation';

interface ProjectItemProps {
  projectImage?: string;
  projectId: number;
  projectName: string;
  organizationName: string;
  amount: string;
  surveyCompleted: boolean;
  beneficiaries?: number;
  hasVolunteeringOpportunity?: boolean;
  deliveryDate?: string;
  primaryButton?: ButtonProps;
  primaryButtonLabel?: string;
  secondaryButton?: ButtonProps;
  secondaryButtonLabel?: string;
  videoThumbnail?: string;
  isFunded?: boolean;
  selectionStatus?: SelectionStatusEnum;
  voteCount?: number;
  showVotes?: boolean;
  nominationCount?: number;
}

export const ProjectItem = ({
  projectImage,
  projectId,
  projectName,
  organizationName,
  amount,
  beneficiaries,
  hasVolunteeringOpportunity,
  deliveryDate,
  primaryButton,
  primaryButtonLabel,
  secondaryButton,
  secondaryButtonLabel,
  videoThumbnail,
  isFunded,
  selectionStatus,
  voteCount,
  showVotes,
  nominationCount,
  surveyCompleted,
}: ProjectItemProps) => {
  const { t } = useTranslation('fund');
  const projectInformation = useMemo(() => {
    const information: ProjectInformationItem[] = [
      {
        title: t('management.project_information.amount'),
        icon: <RiMoneyPoundCircleLine size={16} />,
        text: amount || 'N/A',
      },
      {
        title: t('management.project_information.beneficiaries'),
        icon: <RiTeamLine size={16} />,
        text: beneficiaries?.toString() || 'N/A',
      },
      {
        title: t('management.project_information.delivery'),
        icon: <RiCalendarLine size={16} />,
        text: deliveryDate || 'N/A',
      },
    ];

    if (hasVolunteeringOpportunity) {
      information.push({
        title: t('management.project_information.volunteering'),
        icon: <RiUserHeartLine size={16} />,
        text: (
          <span className={styles.volunteeringAvailable}>
            {t('management.project_information.available')}
          </span>
        ),
      });
    }

    return information;
  }, [amount, beneficiaries, deliveryDate, hasVolunteeringOpportunity, t]);

  return (
    <div className={styles.tileWrapper}>
      <Tile className={styles.tile}>
        <div className={styles.container}>
          <div className={styles.leftSection}>
            <div
              className={styles.thumbnail}
              style={{ backgroundImage: `url(${projectImage || videoThumbnail})` }}
            >
              {!videoThumbnail && !projectImage && <RiImageLine style={{ fontSize: '32px' }} />}
              {videoThumbnail && <RiPlayCircleLine className={styles.videoWatermark} />}
            </div>

            <div className={styles.titleContainer}>
              <Link href={Routes.PROJECT_DETAILS(projectId.toString())}>
                <h3 className={styles.projectName}>{projectName}</h3>
              </Link>
              <div className={styles.organizationName}>{organizationName}</div>
              {voteCount != null && showVotes && (
                <div className={styles.voteCount}>
                  {t('management.vote_count', { count: voteCount })}
                </div>
              )}
            </div>
            <ProjectInformation projectInformation={projectInformation} />
          </div>
          <div className={styles.rightSection}>
            {nominationCount !== undefined && nominationCount > 0 && (
              <StatusChip label={t('management.campaign_badge')} color="primary" size="small" />
            )}
            {surveyCompleted && (
              <StatusChip label={t('management.survey_completed')} color="primary" size="small" />
            )}
            {selectionStatus && <InvitationStatusChip status={selectionStatus} />}
            {secondaryButton && <Button {...secondaryButton}>{secondaryButtonLabel}</Button>}
            {primaryButton && <Button {...primaryButton}>{primaryButtonLabel}</Button>}
          </div>
        </div>
      </Tile>
      {isFunded && (
        <Tooltip title={t('management.project_unavailable_tooltip') as string}>
          <div className={styles.tileOverlay}>
            <span>{t('management.project_unavailable')}</span>
          </div>
        </Tooltip>
      )}
      {selectionStatus === SelectionStatusEnum.UNAVAILABLE && (
        <Tooltip title={t('management.project_unavailable_tooltip') as string}>
          <div className={styles.tileOverlay}>
            <span>{t('management.project_unavailable')}</span>
          </div>
        </Tooltip>
      )}
      {selectionStatus === SelectionStatusEnum.NO_LONGER_MATCHES && (
        <Tooltip title={t('management.selection_no_longer_matches_tooltip') as string}>
          <div className={styles.tileOverlay}>
            <span>{t('management.selection_no_longer_matches')}</span>
          </div>
        </Tooltip>
      )}
    </div>
  );
};
